import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";
import DataGrid, {
  Column,
  Pager,
  Paging,
  Selection,
  Editing,
  Grouping,
  GroupPanel,
  Export,
  Sorting,
  Toolbar,
  Item,
  ColumnChooser,
  Scrolling,
} from "devextreme-react/data-grid";
import CustomStore from "devextreme/data/custom_store";
import INTAKE_API from "api/intakeApi";
import {
  capitalizeWords,
  onExporting,
  onExportingExcel,
  toLocalISOString,
} from "utils/helpers";
import { useDispatch, useSelector } from "react-redux";
import CheckBox from "devextreme-react/check-box"; // Assuming DevExtreme
import { useNavigate, useLocation } from "react-router-dom";
import notify from "devextreme/ui/notify"; // Import DevExtreme notification
import { useQueryClient } from "@tanstack/react-query";
import "./OverviewTable.css";
import { apiGet } from "api/api-intake";
import { useDelete, useFetch, usePost } from "@hooks/useBatchIntakeApi";
import STATISTICS_API from "api/statisticsApi";
import "devextreme/ui/dialog"; // Import DevExtreme dialog
import { confirm } from "devextreme/ui/dialog"; // Import confirm dialog
import {
  Button,
  DateBox,
  LoadIndicator,
  Popup,
  RadioGroup,
  TextBox,
} from "devextreme-react";
import Loader from "@common-components/loader/Loader";
import { ThemeContext } from "theme";

const exportFormats = ["xlsx"]; // ✅ Add "xlsx" for Excel export

export default function OverviewTable({ onExpand, isExpanded }) {
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [editingRowKey, setEditingRowKey] = useState(null); // Track the row being edited
  const [selectedIntakeIds, setSelectedIntakeIds] = useState([]); // ✅ Track selected intake IDs
  const [loadingAfterDelete, setLoadingAfterDelete] = useState(false); // Add state for loader
  const [selectedFilter, setSelectedFilter] = useState("");
  const [advanceFilterVisible, setAdvanceFilterVisible] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isDateRangeExist, setIsDateRangeExist] = useState(false);
  const [batchNumber, setBatchNumber] = useState(null);
  const [supplierLocation, setSupplierLocation] = useState(null);
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);

  const theme = useContext(ThemeContext);
  const navigate = useNavigate();
  const batchID = useSelector((state) => state.overview.batchId);
  const gridRef = useRef(null);
  // Build claims filter with 'search' param only, always trim spaces from both ends
  const getClaimsFilter = () => {
    let filter = {};
    if (isAdvancedSearch) {
      if (selectedFilter === "Supplier") {
        filter = { search: supplier ? supplier.trim() : null };
      } else if (selectedFilter === "BatchNumber") {
        filter = { search: batchNumber ? batchNumber.trim() : null };
      } else if (selectedFilter === "Location") {
        filter = { search: supplierLocation ? supplierLocation.trim() : null };
      } else if (selectedFilter === "IntakeDate") {
        filter = {
          search: null,
          startDate: startDate ? toLocalISOString(startDate) : null,
          endDate: endDate ? toLocalISOString(endDate) : null,
        };
      }
    } else {
      // Get the current searchPanel text from the DataGrid
      let searchText = null;
      if (gridRef.current && gridRef.current.instance) {
        const gridInstance = typeof gridRef.current.instance === 'function'
          ? gridRef.current.instance()
          : gridRef.current.instance;
        if (gridInstance && gridInstance.option) {
          searchText = gridInstance.option('searchPanel.text') || null;
        }
      }
      filter = { search: searchText ? searchText.trim() : null };
    }
    return filter;
  };

  const { data: totalBatches, loading: totalBatchesLoading } = useFetch(
    "getTotalBatches",
    STATISTICS_API.getTotalBatches
  );
  const { data: totalClaims, loading: totalClaimsLoading, refetch: refetchTotalClaims } = useFetch(
    ["getTotalCalims", getClaimsFilter()],
    STATISTICS_API.getTotalClaims,
    getClaimsFilter()
  );
  const { data: averagePrice, loading: averagePriceLoading } = useFetch(
    "getAveragePrice",
    STATISTICS_API.getAveragePrice
  );

  // console.log("llll", batchData)
  const deleteMutation = useDelete("deleteBatch", INTAKE_API.deleteBatchIntake);
  const [selectedIds, setSelectedIds] = useState([]); // ✅ Track selected row IDs
  const [selectedBatchId, setSelectedBatchId] = useState([]);
  const dispatch = useDispatch();
  const postMutation = usePost(
    "postSelectedBatches",
    INTAKE_API.selectedBatches
  );

  const [sortSelected, setSortSelected] = useState(false);
  const location = useLocation(); // Get location state

  // Store selected row key on selection
  const onSelectionChanged = async (e) => {
    console.log("ddddd", e.selectedRowsData);
    const selectedRow = e.selectedRowsData[0];
    const selectedRowKey = e.selectedRowKeys[0];

    localStorage.setItem("batchNum", selectedRow?.batchNumber);
    localStorage.setItem("supplierId", selectedRow?.supplier?.supplierID);
    localStorage.setItem("supplierName", selectedRow?.supplier?.name);
    localStorage.setItem("selectedRow", JSON.stringify(selectedRow));
    localStorage.setItem("selectedRowKey", JSON.stringify(selectedRowKey));

    const selectedData = await e.component.getVisibleRows();
    const keys = await e.component.getSelectedRowKeys();
    console.log(await e.component.getSelectedRowKeys(), "selected rows");
  };

  // Restore selection on grid content ready (fires on paging, sorting, etc.)
  const handleContentReady = useCallback((e) => {
    const savedKey = localStorage.getItem("selectedRowKey");
    if (savedKey && savedKey !== "undefined") {
      const parsedKey = JSON.parse(savedKey);
      if (parsedKey !== null) {
        e.component.selectRows([parsedKey], false);
      }
    }
  }, []);

  // Restore selection when navigating back to this page
  useEffect(() => {
    // Wait for gridRef to be available and grid to be mounted
    const timer = setTimeout(() => {
      const savedKey = localStorage.getItem("selectedRowKey");
      if (
        gridRef.current &&
        gridRef.current.instance &&
        typeof gridRef.current.instance === "function"
      ) {
        const gridInstance = gridRef.current.instance();
        if (savedKey && savedKey !== "undefined") {
          const parsedKey = JSON.parse(savedKey);
          if (parsedKey !== null) {
            gridInstance.selectRows([parsedKey], false);
          }
        }
      }
    }, 200); // Delay to ensure grid is mounted

    return () => clearTimeout(timer);
  }, [location]);

  // ✅ Function to trigger API call
  const sendSelectedIds = async () => {
    const selected = gridRef.current.instance().getSelectedRowKeys();
    const selectedRows = gridRef.current.instance().getSelectedRowsData();

    // Check if any selected row has qcp or ccp
    const hasQcpOrCcp = selectedRows?.some((row) => row?.qcp || row?.ccp);
    if (hasQcpOrCcp) {
      notify({
        message: "Batch contains QCP/CCP data; deletion restricted",
        type: "error",
        displayTime: 2000,
        position: {
          my: "top right",
          at: "top right",
          of: window,
        },
      });
      return;
    }

    console.log("Selected IDs:", selected, selectedRows);

    if (selected?.length === 0) {
      notify({
        message: "No rows selected for deletion",
        type: "error",
        displayTime: 2000,
        position: {
          my: "top right",
          at: "top right",
          of: window,
        },
      });
      return;
    }

    const result = await confirm(
      "Are you sure you want to delete the selected record?",
      "Confirm Deletion"
    ); // Show confirmation dialog
    if (!result) {
      return; // Exit if user cancels
    }

    try {
      setLoadingAfterDelete(true); // Show loader
      await deleteMutation.mutateAsync({ intakeID: selected }); // Perform delete operation
      console.log("Successfully deleted selected IDs:", selected);
      notify({
        message: "Selected rows deleted successfully",
        type: "success",
        displayTime: 2000,
        position: {
          my: "top right",
          at: "top right",
          of: window,
        },
      });

      gridRef.current?.instance()?.refresh(); // Refresh the grid
      queryClient.invalidateQueries("getTotalBatches"); // Optionally update statistics

      setTimeout(() => {
        setLoadingAfterDelete(false); // Hide loader after 1 second
      }, 1000);
    } catch (error) {
      console.error("Error deleting selected IDs:", error);
      notify({
        message: "Error deleting selected rows",
        type: "error",
        displayTime: 2000,
        position: {
          my: "top right",
          at: "top right",
          of: window,
        },
      });
      setLoadingAfterDelete(false); // Hide loader in case of error
    }
  };

  let search = null;
  const queryClient = useQueryClient(); // ✅ Import from 'react-query'

  console.log("location", location.state);
  const dataSource = new CustomStore({
    key: "batchIntakeID",
    load: async (loadOptions) => {
      const take = loadOptions.take ?? pageSize;
      const pageNo = loadOptions.skip ? Math.floor(loadOptions.skip / take) : 0;
      // Get search text from DataGrid searchPanel
      const searchText = loadOptions.searchValue || (gridRef.current?.instance()?.option("searchPanel.text") ?? null);
      // ...existing code...
      try {
        if (sortSelected && loadOptions.sort?.length > 0) {
          const { selector, desc } = loadOptions.sort[0];

          setIsAdvancedSearch(false);
          const response = await queryClient.fetchQuery({
            queryKey: ["batchIntake", pageNo, pageSize, searchText],
            queryFn: () =>
              apiGet(INTAKE_API.getBatchIntake, {
                PageNo: pageNo,
                PageSize: pageSize,
                search: searchText ? searchText : null, // Pass search text to API for server-side search
                sortField:
                  typeof selector === "function"
                    ? loadOptions?.sort[0]?.selector?.context?.column?.caption
                    : selector,
                sortOrder: desc ? "desc" : "asc",
              }),
          });

          const intakeData = response?.data || [];
          if (location.state) {
            navigate(location.pathname, { state: false });
          }
          setTotalCount(response?.info?.totalRecordsCount || 0);
          return {
            data: intakeData,
            totalCount: response?.info?.totalRecordsCount || 0,
          };

          
        } else {
          let filter = { search: searchText || getSearchCriteria() };

          if (selectedFilter === "IntakeDate" && isAdvancedSearch) {
            filter = {
              search: null,
              ...(startDate ? { startDate: toLocalISOString(startDate) } : {}),
              ...(endDate ? { endDate: toLocalISOString(endDate) } : {}),
            };
          }

        

          console.log(filter, "filter");
          const response = await queryClient.fetchQuery({
            queryKey: ["batchIntake", pageNo, pageSize, searchText],
            queryFn: () =>
              apiGet(INTAKE_API.getBatchIntake, {
                PageNo: pageNo,
                PageSize: pageSize,
                ...filter,
              }),
          });

          const intakeData = response?.data || [];
          if (location.state) {
            navigate(location.pathname, { state: false });
          }
          setTotalCount(response?.info?.totalRecordsCount || 0);
          return {
            data: intakeData,
            totalCount: response?.info?.totalRecordsCount || 0,
          };
        }
        // }

        // ✅ Set location.state to false after API call
      } catch (err) {
        console.error("Failed to load data:", err);
        throw err;
      }
    },
  });

  const getSearchCriteria = () => {
    if (isAdvancedSearch && selectedFilter === "Supplier") {
      return supplier ? supplier.trim() : null;
    }
    if (isAdvancedSearch && selectedFilter === "BatchNumber") {
      return batchNumber ? batchNumber.trim() : null;
    }
    if (isAdvancedSearch && selectedFilter === "Location") {
      return supplierLocation ? supplierLocation.trim() : null;
    }
    if (isAdvancedSearch && selectedFilter === "IntakeDate") {
      return null;
    }
    if (!isAdvancedSearch) {
      return null;
    }
  };

  // Helper to build advanced filter object for claims API
 

  useEffect(() => {
    // Invalidate the totalBatches query to ensure it fetches fresh data
    queryClient.invalidateQueries("getTotalBatches");
  }, [location]); // Trigger when location changes

  const handleExportSelected = (e) => {
    const selected = [
      ...new Set(gridRef.current.instance().getSelectedRowKeys()),
    ]; // Ensure unique selected IDs
    console.log("Selected IDs:", selected, e.selectedRowsOnly);

    if (e.format === "xlsx" || e.format === "pdf") {
      if (e.selectedRowsOnly && selected.length === 0) {
        notify({
          message: "Please select at least one row before exporting",
          type: "error",
          displayTime: 2000,
          position: {
            my: "top right",
            at: "top right",
            of: window,
          },
        });
        e.cancel = true; // Prevent export
        return;
      }

      // Call the appropriate export function
      if (e.format === "xlsx") {
        onExportingExcel({ ...e, selectedRows: selected }); // Pass unique selected rows
      } else if (e.format === "pdf") {
        onExporting({ ...e, selectedRows: selected }); // Pass unique selected rows
      }
    }
  };

  const handleRowRemoved = useCallback(() => {
    setEditingRowKey(null);
  }, []);

  const handleOptionChanged = useCallback((e) => {
    if (e.fullName === "paging.pageSize") {
      setPageSize(e.value);
    }
  }, []);

  const handleAddNewRecord = () => {
    navigate("/quality/fillet/add-intake");

    // Invalidate the totalBatches query to refresh the data
    queryClient.invalidateQueries("getTotalBatches");
  };
  // console.log("dataSource", theme?.theme);
  return (
    <>
      {loadingAfterDelete ? ( // Show loader if loadingAfterDelete is true
        <div className="loader-container">
          <Loader />
        </div>
      ) : totalBatchesLoading || totalClaimsLoading || averagePriceLoading ? ( // Fix loader condition
        <div className="loader-container">
          <Loader />
        </div>
      ) : (
        <React.Fragment>
          
          <Popup
            visible={advanceFilterVisible}
            onHiding={() => setAdvanceFilterVisible(false)}
            dragEnabled={false}
            showCloseButton={true}
            showTitle={true}
            title={`Advanced Search`}
            width={"45%"}
            height={'auto'}
          >
            <div className="h-full flex justify-between flex-col">
              <div className="flex gap-3 items-start">
                {/* Reset link on the left side */}
              
                <RadioGroup
                  // disabled={isEdit}
                  layout="horizontal"
                  items={[
                    {
                      id: "Supplier",
                      text: (
                        <TextBox
                          value={supplier}
                          onValueChanged={(e) => {
                            setSupplier(e.value);
                          }}
                          placeholder="Enter Supplier Name"
                          showClearButton={true}
                          label="Supplier Name"
                          labelMode="visible"
                          // className="w-full"
                          // onEnterKey={() => {
                          //   if (pid.length > 0) {
                          //     setIsPidExist(true);
                          //     // setAdvanceFilterVisible(false);
                          //     setSupplier(null);
                          //     setBatchNumber(null);
                          //   }
                          // }}
                        />
                      ),
                    },
                    {
                      id: "BatchNumber",
                      text: (
                        <TextBox
                          value={batchNumber}
                          onValueChanged={(e) => {
                            setBatchNumber(e.value);
                          }}
                          placeholder="Enter Batch Number"
                          showClearButton={true}
                          label="Batch Number"
                          labelMode="visible"
                          // className="w-full"
                          // onEnterKey={() => {
                          //   if (pid.length > 0) {
                          //     setIsPidExist(true);
                          //     // setAdvanceFilterVisible(false);
                          //     setSupplier(null);
                          //     setBatchNumber(null);
                          //   }
                          // }}
                        />
                      ),
                    },
                    {
                      id: "Location",
                      text: (
                        <TextBox
                          value={supplierLocation}
                          onValueChanged={(e) => {
                            setSupplierLocation(e.value);
                          }}
                          placeholder="Location"
                          showClearButton={true}
                          label="Location"
                          labelMode="visible"
                          // className="w-full"
                          // onEnterKey={() => {
                          //   if (pid.length > 0) {
                          //     setIsPidExist(true);
                          //     // setAdvanceFilterVisible(false);
                          //     setSupplier(null);
                          //     setBatchNumber(null);
                          //   }
                          // }}
                        />
                      ),
                    },
                    {
                      id: "IntakeDate",
                      text: (
                        <div
                          style={{
                            display: "flex",
                            gap: "1rem",
                            marginTop: 10,
                          }}
                        >
                          <div className="flex flex-col items-start">
                            <DateBox
                              type="datetime"
                              value={startDate}
                              onValueChanged={(e) => {
                                setStartDate(e.value);
                              }}
                              label="Start Date & Time"
                              labelMode="visible"
                              placeholder="dd/mm/yyyy --:-- --Start"
                              displayFormat="yyyy-MM-dd HH:mm" // 👈 24-hour format
                              showClearButton={true}
                            />
                          </div>
                          <div className="flex flex-col items-start">
                            <DateBox
                              type="datetime"
                              value={endDate}
                              onValueChanged={(e) => {
                                setEndDate(e.value);
                              }}
                              label="End Date & Time"
                              labelMode="visible"
                              disabled={!startDate}
                              placeholder="dd/mm/yyyy --:-- --"
                              showClearButton={true}
                              displayFormat="yyyy-MM-dd HH:mm" // 👈 24-hour format
                              min={startDate} // Prevent selecting an end before the start
                            />
                          </div>
                        </div>
                      ),
                    },
                  ]}
                  valueExpr="id"
                  displayExpr="text"
                  value={selectedFilter}
                  onValueChanged={(e) => setSelectedFilter(e.value)}
                  itemRender={(item) => <div>{item.text}</div>}
                />
                <div>
                 
                </div>
              </div>
              <div className="flex w-full justify-end mt-3">
                <Button
                  text="Contained"
                  type="default"
                  onClick={() => {
                    setSortSelected(false);
                    setAdvanceFilterVisible(false);
                    // Reset other filters based on selectedFilter
                    if (selectedFilter === "Supplier") {
                      setBatchNumber(null);
                      setSupplierLocation(null);
                      setEndDate(null);
                      setStartDate(null);
                    } else if (selectedFilter === "BatchNumber") {
                      setSupplier(null);
                      setSupplierLocation(null);
                      setEndDate(null);
                      setStartDate(null);
                    } else if (selectedFilter === "Location") {
                      setBatchNumber(null);
                      setSupplier(null);
                      setEndDate(null);
                      setStartDate(null);
                    } else if (selectedFilter === "IntakeDate") {
                      setBatchNumber(null);
                      setSupplier(null);
                      setSupplierLocation(null);
                    }
                    gridRef.current?.instance()?.clearSorting();
                    setIsAdvancedSearch(true);
                    gridRef.current?.instance().refresh(); // Trigger DataGrid reload
                    refetchTotalClaims(); // Call claims API with filters
                  }}
                >
                  Search
                </Button>
              </div>
            </div>
          </Popup>
 
          <DataGrid
            className="dx-card content-block border-0"
            dataSource={dataSource}
            keyExpr="batchIntakeID"
            showBorders={false}
            ref={gridRef}
            columnAutoWidth={true}
            columnHidingEnabled={true}
            hoverStateEnabled={true}
            rowAlternationEnabled={false}
            allowColumnReordering={true}
            // onRowPrepared={(e) => {
            //   if (e.rowType === "data") {
            //     if (theme?.theme === "light") {
            //       const row = e.rowElement;
            //       row.style.backgroundColor =
            //         e.rowIndex % 2 === 0 ? "#ffffff" : "#f3f4f6";
            //       row.classList.add("cursor-pointer", "custom-data-row");
            //     } else {
            //       const row = e.rowElement;
            //       row.style.backgroundColor =
            //         e.rowIndex % 2 === 0 ? "#003158" : "#00233f";
            //       row.classList.add("cursor-pointer", "custom-data-row");
            //     }
            //   }
            // }}
            onSelectionChanged={onSelectionChanged}
            onContentReady={handleContentReady} // <-- Add this line
            remoteOperations={{
              paging: true,
              sorting: true,
              filtering: true // Enable server-side filtering (search)
            }}
            totalCount={totalCount}
            rowDragging={{ allowReordering: false, showDragIcons: false }}
            sorting={{ mode: "multiple", showSortIndexes: false }}
            onOptionChanged={(e) => {
              if (e.fullName === "paging.pageSize") {
                setPageSize(e.value);
              }
              if (e.value === "asc" || e.value === "desc") {
                setSortSelected(true);
              }
              // Debounce and restore focus for searchPanel input
              if (e.fullName === "searchPanel.text") {
                if (!window.__overviewTableSearchDebounce) {
                  window.__overviewTableSearchDebounce = {};
                }
                clearTimeout(window.__overviewTableSearchDebounce.timer);
                const searchText = e.value;
                window.__overviewTableSearchDebounce.timer = setTimeout(() => {
                  gridRef.current?.instance()?.refresh();
                  // Call claims API with search text
                  refetchTotalClaims();
                  // Restore focus to the search box after refresh
                  setTimeout(() => {
                    const searchInput = document.querySelector('.dx-datagrid-search-panel input');
                    if (searchInput) {
                      searchInput.focus();
                    }
                  }, 100);
                }, 3000); // 3 seconds debounce
              }
            }}
            onExporting={handleExportSelected}
            searchPanel={{ visible: true, highlightCaseSensitive: true }}
            loadPanel={{
              enabled: true,
              text: "Processing...",
              showIndicator: false,
              //  shading: true,
              shadingColor: "rgba(255, 255, 255, 0.7)",
            }}
          >
            <Paging
              enabled={true}
              pageSize={pageSize}
              defaultPageSize={pageSize}
            />
            <Pager
              visible={totalCount > 10} // ✅ Show only if records are more than 10
              showPageSizeSelector={true}
              allowedPageSizes={[10, 100, 500, 1000]}
              showInfo={true}
              showNavigationButtons={true}
            />
            <Editing mode="row" allowUpdating={true} allowDeleting={true} />{" "}
            {/* ✅ Enable Editing */}
            <Scrolling mode="standard" />
            <Sorting mode="multiple" />{" "}
            {/* ✅ Ensure sorting arrows are always visible */}
            <Selection
              mode="multiple"
              selectAllMode="page" // or "allPages" if needed
              showCheckBoxesMode="always" // or "always"
              deferred={false} //
            />{" "}
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={true} />
            <Export
              enabled={true}
              formats={exportFormats}
              allowExportSelectedData={true} // Keep selected rows for Excel
            />
            <Editing mode="row" allowUpdating={true} allowDeleting={true} />
            <ColumnChooser enabled={true} />
            {/* <Column
              dataField='supplier.name'
              caption="Supplier"
              sortOrder="desc"
              allowSorting={false}
           
              alignment="left"
              calculateCellValue={(data) => capitalizeWords(data.supplier?.name)}


            /> */}
            <Column
              dataField="supplier.name" // ✅ Use supplier.name for sorting
              caption="Supplier"
              allowSorting={true}
              sortOrder="desc"
              alignment="left"
              cellRender={(cellData) => {
                const text = cellData.value || "Unknown"; // Use cellData.value directly
                return (
                  <div
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "140px",
                    }}
                    title={text}
                  >
                    {text}
                  </div>
                );
              }}
            />
            <Column
              allowSorting={true}
              dataField="batchNumber"
              sortOrder="desc"
              caption="Batch No."
              alignment="left"

              // ✅ Enable sorting for this column
            />
            <Column
              dataField="location.addressLine"
              caption="Location"
              sortOrder="desc"
              alignment="left"
              allowSorting={true}
              calculateDisplayValue={(rowData) =>
                rowData.location?.addressLine
                  ? capitalizeWords(rowData.location.addressLine)
                  : " "
              }
              cellRender={(cellData) => {
                const text = cellData.displayValue || "Unknown"; // use displayValue here
                return (
                  <div
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "140px",
                    }}
                    title={text}
                  >
                    {text}
                  </div>
                );
              }}
            />
            <Column
              dataField="statusID"
              caption="Status"
              alignment="left"
              sortOrder="desc"
              allowSorting={true}
              // ✅ Enable sorting for this column
              calculateDisplayValue={(rowData) =>
                rowData.status?.description || "Unknown"
              }
            />
            <Column
              dataField="intakeDate"
              caption="Date"
              dataType="string"
              sortOrder="desc"
              alignment="left"
              //
             
              customizeText={(cellInfo) => {
                if (!cellInfo.value) {
                  return "Unknown"; // Handle null or undefined date values
                }
                console.log("cellInfo.value", cellInfo?.value);
                const date = cellInfo?.value;
                console.log("date", date);
                return date?.split("T")[0]; // Format as yyyy-mm-dd
              }}
            />
            <Column
              dataField="qcp"
              caption="QCP"
              width={100}
              dataType="boolean"
              allowSorting={true}
              sortOrder="desc"
              cellRender={(cellData) => (
                <CheckBox value={!!cellData.value} readOnly={true} /> // Ensure value is boolean
              )}
            />
            <Column
              dataField="ccp"
              caption="CCP"
              width={100}
              dataType="boolean"
              sortOrder="desc"
              allowSorting={true}
              cellRender={(cellData) => (
                <CheckBox value={!!cellData.value} readOnly={true} /> // Ensure value is boolean
              )}
            />
            <Column
              sortOrder="desc"
              allowSorting={true}
              dataField="pricePerKG"
              caption="Price / kg (€)"
              dataType="number"
              alignment="left"

              // ✅ Enable sorting for this column
            />
            <Column
              dataField=""
              caption="Added Value"
              sortOrder="desc"
              calculateDisplayValue={(rowData) =>
                rowData.claimAmount !== 0
                  ? `${((rowData?.claimAmount / (rowData?.pricePerKG * rowData?.volume)) * 100).toFixed(2)}%`
                  : "0"
              }
              alignment="left"
              allowSorting={true}
              cellRender={(cellData) => {
                const value = cellData.displayValue;
                const isClaimZero = cellData.data.claimAmount === 0;
                return (
                  <div
                    className={`text-sm font-medium rounded text-center p-1    ${isClaimZero ? "bg-gray-300" : "bg-[#35c759] text-white"}`}
                  >
                    {value}
                  </div>
                );
              }}
            />
            <Column
              sortOrder="desc"
              allowSorting={true}
              dataField="claimAmount"
              caption="Claim Amount (€)"
              dataType="number"
              alignment="left"

              // ✅ Enable sorting for this column
            />
            <Column
              dataField=""
              caption="Change"
              sortOrder="desc"
              calculateDisplayValue={(rowData) =>
                rowData.claimAmount !== 0
                  ? `${(rowData?.pricePerKG / rowData?.claimAmount).toFixed(2)}%`
                  : "0"
              }
              alignment="left"
              allowSorting={true}
              cellRender={(cellData) => {
                const value = cellData.displayValue;
                const isClaimZero = cellData.data.claimAmount === 0;
                return (
                  <div
                    className={`text-sm font-medium rounded text-center p-1    ${isClaimZero ? "bg-gray-300" : "bg-[#35c759] text-white"}`}
                  >
                    {value}
                  </div>
                );
              }}
            />
            <Column
              dataField="productSize.description"
              caption="Size (kg)"
              sortOrder="desc"
              allowSorting={true}
              calculateDisplayValue={(rowData) =>
                rowData.productSize?.description || "Unknown"
              }
              alignment="left"
            />
            <Column
              sortOrder="desc"
              dataField="productQuality.description"
              caption="Quality"
              calculateDisplayValue={(rowData) =>
                capitalizeWords(rowData.productQuality?.description) ||
                "Unknown"
              }
              alignment="left"
              allowSorting={true}
            />
            <Column
              sortOrder="desc"
              allowSorting={true}
              dataField="purchaseRef"
              caption="Purchase Ref"
              alignment="left"
            />
            <Column
              caption="Action"
              type="buttons"
              sortOrder="desc"
              allowSorting={true}
              visible={false}
              buttons={[]} // Remove the "Edit" button from the column
            />
            <Toolbar>
              <Item name="searchPanel" location="before" />
              <Item
                location="center"
                render={() => (
                  <div className="lg:grid grid-cols-3 gap-2 hidden">
                    <div>
                      Total Batches:{" "}
                      <span className="font-medium">{totalCount}</span>
                    </div>
                    <div>
                      Average Price/kg (€):{" "}
                      <span className="font-medium">
                        {" "}
                        {averagePrice?.data?.toFixed(2)}
                      </span>
                    </div>
                    <div>
                      Total Claims:{" "}
                      <span className="font-medium">{totalClaims?.data}</span>
                    </div>
                    
                  </div>
                )}
              />
              <Item
                render={() => (
                  <a
                    className="pt-2 cursor-pointer"
                    onClick={() => {
                      setAdvanceFilterVisible(
                        (advanceFilterVisible) => !advanceFilterVisible
                      );
                      // setIsAdvancedSearch(false); // <-- Remove this line to prevent API call on open
                    }}
                  >
                    Advanced Search
                  </a>
                )}
                location="after"
              />
                  <Item
                render={() => (
                  <a
                    className="text-blue-600 underline cursor-pointer ml-2"
                    style={{ alignSelf: "flex-start" }}
                    onClick={() => {
                      setSupplier(null);
                      setBatchNumber(null);
                      setSupplierLocation(null);
                      setStartDate(null);
                      setEndDate(null);
                      setSelectedFilter("");
                      setIsAdvancedSearch(false); // Ensure search param is null
                      gridRef.current?.instance().refresh(); // Refresh grid after reset
                      refetchTotalClaims(); // Call claims API with cleared filters
                    }}
                  >
                    Reset
                  </a>
                )}
              />
              <Item
                widget="dxButton"
                onClick={handleAddNewRecord}
                options={{ icon: "add", text: "", hint: "Add New Record" }}
                location="after"
              />
              <Item
                widget="dxButton"
                onClick={sendSelectedIds}
                options={{
                  icon: "trash",
                  text: "",
                  hint: "Delete Selected Record",
                }}
                location="after"
              />
              <Item
                widget="dxButton"
                onClick={() => {
                  let totalSelected = gridRef.current
                    .instance()
                    .getSelectedRowKeys();
                  const selectedRow = gridRef.current
                    .instance()
                    .getSelectedRowsData()[0];
                  console.log("selectedRow", selectedRow);

                  if (totalSelected.length > 1) {
                    notify({
                      message: "Please select only one row to edit",
                      type: "error",
                      displayTime: 2000,
                      position: {
                        my: "top right",
                        at: "top right",
                        of: window,
                      },
                    });
                    return;
                  }

                  if (selectedRow) {
                    navigate(
                      `/quality/fillet/overview/edit/${selectedRow.batchIntakeID}`,
                      {
                        state: selectedRow,
                      }
                    );
                  } else {
                    notify({
                      message: "Please select a row to edit",
                      type: "error",
                      displayTime: 2000,
                      position: {
                        my: "top right",
                        at: "top right",
                        of: window,
                      },
                    });
                  }
                }}
                options={{
                  icon: "edit",
                  text: "",
                  hint: "Edit Selected Record",
                }}
                location="after"
              />
              <Item name="exportButton" showText="always" />
            </Toolbar>
            div
          </DataGrid>
        </React.Fragment>
      )}
    </>
  );
}
